/**
 * Find channels.
 *
 * The screen behind Explore. One search field over every channel on the
 * platform, with the most followed shown before anything is typed — an
 * empty discovery screen is the one place where showing nothing is clearly
 * the wrong answer.
 *
 * Searching is debounced rather than fired per keystroke. This query scans
 * names and descriptions across the whole table, and a request per character
 * is how discovery becomes the most expensive screen in the app.
 */

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { channels as channelService } from '@zivapp/shared'
import { SuggestionRow, FollowedRow } from '../components/channels/ChannelRows.jsx'
import './ExploreChannels.css'

const DEBOUNCE_MS = 250

export default function ExploreChannels () {
  const navigate = useNavigate()

  const [term, setTerm] = useState('')
  const [results, setResults] = useState([])
  const [isLoading, setLoading] = useState(true)
  const [following, setFollowing] = useState(null)

  useEffect(() => {
    let alive = true
    const timer = setTimeout(async () => {
      try {
        const found = await channelService.search(term)
        if (alive) setResults(found)
      } catch {
        if (alive) setResults([])
      } finally {
        if (alive) setLoading(false)
      }
    }, DEBOUNCE_MS)

    return () => { alive = false; clearTimeout(timer) }
  }, [term])

  const follow = async channel => {
    setFollowing(channel.id)
    try {
      const updated = await channelService.follow(channel.id)
      setResults(list => list.map(c => (c.id === channel.id ? updated : c)))
    } finally {
      setFollowing(null)
    }
  }

  return (
    <div className="exc">
      <header className="exc__bar">
        <button
          type="button"
          className="exc__back"
          onClick={() => navigate('/updates')}
          aria-label="Back"
        >
          <svg viewBox="0 0 24 24" fill="none">
            <path d="M15 5 8 12l7 7" stroke="currentColor" strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <h1 className="exc__title">Find channels</h1>
      </header>

      <div className="exc__search">
        <svg className="exc__search-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="1.9" />
          <path d="m16.5 16.5 4 4" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
        </svg>
        <input
          className="exc__input"
          value={term}
          onChange={e => setTerm(e.target.value)}
          placeholder="Search channels"
          aria-label="Search channels"
          data-testid="channel-search"
          autoFocus
        />
      </div>

      <div className="exc__scroll">
        {!term.trim() && results.length > 0 && (
          <h2 className="exc__heading">Most followed</h2>
        )}

        {results.map(channel => (
          /* A channel you already follow keeps its ordinary row — offering
             Follow on something you follow is the kind of small wrongness
             that makes a list look untrustworthy. */
          channel.isFollowing
            ? <FollowedRow key={channel.id} channel={channel} />
            : (
              <SuggestionRow
                key={channel.id}
                channel={channel}
                onFollow={follow}
                busy={following === channel.id}
              />
            )
        ))}

        {!isLoading && results.length === 0 && (
          <p className="exc__empty">
            {term.trim()
              ? `No channel matches “${term.trim()}”.`
              : 'There are no channels yet. Create the first one.'}
          </p>
        )}
      </div>

      <div className="exc__footer">
        <button
          type="button"
          className="exc__create"
          onClick={() => navigate('/channels/new')}
        >
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
          Create channel
        </button>
      </div>
    </div>
  )
}
